import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, User, Users, Calendar, X, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
} from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RicercaGlobale({ open, onOpenChange }) {
  const [query, setQuery] = useState('');
  const [risultati, setRisultati] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Focus input when dialog opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (query.length < 2) {
      setRisultati(null);
      return;
    }

    const timer = setTimeout(() => {
      fetchRisultati();
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const fetchRisultati = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/ricerca?q=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRisultati(data);
      }
    } catch (error) {
      console.error('Errore ricerca:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (item) => {
    if (item.link) {
      navigate(item.link);
      onOpenChange(false);
      setQuery('');
      setRisultati(null);
    }
  };

  const getIcon = (tipo) => {
    switch (tipo) {
      case 'volontario': return <User className="h-4 w-4 text-blue-500" />;
      case 'socio': return <Users className="h-4 w-4 text-green-500" />;
      case 'evento': return <Calendar className="h-4 w-4 text-purple-500" />;
      default: return <User className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatoColor = (stato) => {
    switch (stato) {
      case 'attivo': return 'bg-green-100 text-green-700';
      case 'sospeso': return 'bg-amber-100 text-amber-700';
      case 'concluso': return 'bg-gray-100 text-gray-700';
      case 'programmato': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] p-0 gap-0">
        {/* Search Input */}
        <div className="flex items-center border-b px-4 py-3">
          <Search className="h-5 w-5 text-gray-400 mr-3" />
          <Input
            ref={inputRef}
            type="text"
            placeholder="Cerca volontari, soci, eventi..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="border-0 focus-visible:ring-0 text-lg p-0 h-auto"
          />
          {query && (
            <button 
              onClick={() => { setQuery(''); setRisultati(null); }}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <X className="h-4 w-4 text-gray-400" />
            </button>
          )}
        </div>

        {/* Results */}
        <div className="max-h-[400px] overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          )}

          {!loading && risultati && risultati.totale === 0 && (
            <div className="text-center py-8 text-gray-500">
              Nessun risultato per "{query}"
            </div>
          )}

          {!loading && risultati && risultati.totale > 0 && (
            <div className="py-2">
              {/* Volontari */}
              {risultati.volontari?.length > 0 && (
                <div className="px-3 py-2">
                  <div className="text-xs font-medium text-gray-500 uppercase mb-2">
                    Volontari ({risultati.volontari.length})
                  </div>
                  {risultati.volontari.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleSelect(item)}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg text-left"
                    >
                      {getIcon(item.tipo)}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{item.nome}</div>
                        <div className="text-sm text-gray-500 truncate">{item.dettaglio}</div>
                      </div>
                      {item.stato && (
                        <span className={`text-xs px-2 py-1 rounded ${getStatoColor(item.stato)}`}>
                          {item.stato}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* Soci */}
              {risultati.soci?.length > 0 && (
                <div className="px-3 py-2 border-t">
                  <div className="text-xs font-medium text-gray-500 uppercase mb-2">
                    Soci ({risultati.soci.length})
                  </div>
                  {risultati.soci.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleSelect(item)}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg text-left"
                    >
                      {getIcon(item.tipo)}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{item.nome}</div>
                        <div className="text-sm text-gray-500 truncate">
                          {item.numero_tessera ? `Tessera: ${item.numero_tessera}` : item.dettaglio}
                        </div>
                      </div>
                      {item.stato && (
                        <span className={`text-xs px-2 py-1 rounded ${getStatoColor(item.stato)}`}>
                          {item.stato}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* Eventi */}
              {risultati.eventi?.length > 0 && (
                <div className="px-3 py-2 border-t">
                  <div className="text-xs font-medium text-gray-500 uppercase mb-2">
                    Eventi ({risultati.eventi.length})
                  </div>
                  {risultati.eventi.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleSelect(item)}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg text-left"
                    >
                      {getIcon(item.tipo)}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{item.nome}</div>
                        <div className="text-sm text-gray-500 truncate">{item.dettaglio}</div>
                      </div>
                      {item.stato && (
                        <span className={`text-xs px-2 py-1 rounded ${getStatoColor(item.stato)}`}>
                          {item.stato}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Empty state */}
          {!loading && !risultati && (
            <div className="text-center py-8 text-gray-500">
              <Search className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p>Digita almeno 2 caratteri per cercare</p>
              <p className="text-sm mt-1">Cerca per nome, cognome, codice fiscale, email...</p>
            </div>
          )}
        </div>

        {/* Footer shortcut hint */}
        <div className="border-t px-4 py-2 bg-gray-50 text-xs text-gray-500 flex items-center justify-between">
          <span>Premi <kbd className="px-1.5 py-0.5 bg-gray-200 rounded">ESC</kbd> per chiudere</span>
          <span>{risultati?.totale || 0} risultati</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
